"use client";

// Preset picker popover — favourites/custom/executive/management tabs, star
// to favourite, manage mode (reorder + create). Shared between the COMMAND
// v2 chat composer (labelled outline trigger) and the global Talk to Command
// drawer (compact icon trigger, via `compact`).

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  BookMarked,
  BookmarkPlus,
  ChevronDown,
  ChevronUp,
  Star,
  X,
} from "lucide-react";
import { useInView } from "react-intersection-observer";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { COMMAND_V2_DIALOG_CONTENT_CLASS } from "@/components/command-team-v2/utils/dialog-styles";

import type { ProtoPreset, ProtoPresetCategory } from "../prototype/types";
import { ChainStepsBadge, PRESET_CATEGORY_META } from "./empty-state";
import { CommandSidebarListSkeleton } from "./command-v2-loading-skeletons";

/** "Save current prompt" popup — title + category + editable prompt text,
 *  prefilled from whatever is in the composer. */
function SavePresetDialog({
  open,
  onOpenChange,
  initialPrompt,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPrompt: string;
  onSave: (input: {
    title: string;
    promptText: string;
    category: ProtoPresetCategory;
  }) => void;
}) {
  const [title, setTitle] = useState("");
  const [promptText, setPromptText] = useState("");
  const [category, setCategory] = useState<ProtoPresetCategory>("custom");

  useEffect(() => {
    if (open) {
      setTitle("");
      setPromptText(initialPrompt);
      setCategory("custom");
    }
  }, [open, initialPrompt]);

  const canSave = title.trim().length > 0 && promptText.trim().length > 0;

  const submit = () => {
    if (!canSave) return;
    onSave({
      title: title.trim(),
      promptText: promptText.trim(),
      category,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, "max-w-md")}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-[9px] bg-violet-100 p-2 dark:bg-violet-900/30">
              <BookmarkPlus className="size-4 text-primary" />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-left text-lg font-semibold tracking-tight">
                Save current prompt
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Reuse it any time from the Presets picker.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Title</Label>
            <Input
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit();
              }}
              placeholder="e.g. Weekly complaints digest"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Prompt</Label>
            <Textarea
              value={promptText}
              onChange={(event) => setPromptText(event.target.value)}
              placeholder="The prompt text this preset will run"
              className="min-h-[96px] text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Category</Label>
            <Select
              value={category}
              onValueChange={(value) => setCategory(value as ProtoPresetCategory)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  Object.entries(PRESET_CATEGORY_META) as [
                    ProtoPresetCategory,
                    { label: string },
                  ][]
                ).map(([value, meta]) => (
                  <SelectItem key={value} value={value}>
                    {meta.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 flex shrink-0 items-center justify-end gap-4 pt-1">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-sm font-medium text-foreground transition-opacity hover:opacity-70"
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="h-10 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Save preset
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type PresetTab = "all" | "favourites" | ProtoPresetCategory;

const PRESET_TABS: { value: PresetTab; label: ReactNode }[] = [
  { value: "all", label: "All" },
  {
    value: "favourites",
    label: <Star className="size-3.5 fill-amber-400 text-amber-400" />,
  },
  { value: "executive", label: "Executive" },
  { value: "management", label: "Management" },
];

export function PresetPopover({
  presets,
  onPick,
  onToggleFavourite,
  onMovePreset,
  onCreatePreset,
  compact = false,
  currentPrompt = "",
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: {
  presets: ProtoPreset[];
  onPick: (preset: ProtoPreset) => void;
  onToggleFavourite: (presetId: string) => void;
  onMovePreset: (presetId: string, direction: "up" | "down") => void;
  onCreatePreset: (input: {
    title: string;
    promptText: string;
    category: ProtoPresetCategory;
  }) => void;
  /** Icon-only trigger for tight composers (Talk to Command drawer). */
  compact?: boolean;
  /** The composer's draft — prefills the "Save current prompt" popup. */
  currentPrompt?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tab, setTab] = useState<PresetTab>("favourites");
  const [manage, setManage] = useState(false);
  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) onLoadMore?.();
  }, [inView, hasNextPage, isFetchingNextPage, onLoadMore]);

  const sorted = useMemo(
    () => [...presets].sort((a, b) => a.order - b.order),
    [presets]
  );
  const list =
    tab === "all"
      ? sorted
      : tab === "favourites"
        ? sorted.filter((preset) => preset.favourite)
        : sorted.filter((preset) => preset.category === tab);

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      {/* squeezed composer: the label span hides (command-composer-label) and
          the tooltip carries the name */}
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              {compact ? (
                <button
                  type="button"
                  aria-label="Presets"
                  className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <BookMarked className="size-4" />
                </button>
              ) : (
                <Button
                  variant="outline"
                  aria-label="Presets"
                  className="h-10 shrink-0 gap-1.5 rounded-full px-3.5 text-sm"
                >
                  <BookMarked className="size-4 text-primary" />
                  <span className="command-composer-label">Presets</span>
                </Button>
              )}
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Presets
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="start" side="top" className="w-[380px] p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">
            {manage ? "My presets" : "Presets"}
          </p>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              className="h-6 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setOpen(false);
                setSaveOpen(true);
              }}
            >
              <BookmarkPlus className="size-3.5" />
              Save current prompt
            </Button>
            <Button
              variant="ghost"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setManage((current) => !current)}
            >
              {manage ? "Done" : "Manage"}
            </Button>
          </div>
        </div>

        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as PresetTab)}
          className="mt-2"
        >
          <TabsList className="grid h-8 w-full grid-cols-4">
            {PRESET_TABS.map((item) => (
              <TabsTrigger
                key={item.value}
                value={item.value}
                className="h-6 text-xs"
              >
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-2 max-h-72 space-y-0.5 overflow-y-auto pr-1">
          {list.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              {tab === "favourites"
                ? "No favourites yet — star a preset to pin it here."
                : "No presets in this category yet."}
            </p>
          ) : null}
          {list.map((preset, index) => (
            <div
              key={preset.id}
              className="group flex items-start gap-2 rounded-xl px-2 py-2 hover:bg-muted/60"
            >
              <button
                type="button"
                onClick={() => onToggleFavourite(preset.id)}
                className="mt-0.5 shrink-0"
                title={preset.favourite ? "Remove favourite" : "Add to favourites"}
              >
                <Star
                  className={cn(
                    "size-4 transition-colors",
                    preset.favourite
                      ? "fill-amber-400 text-amber-400"
                      : "text-muted-foreground/40 hover:text-muted-foreground"
                  )}
                />
              </button>
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => {
                  onPick(preset);
                  setOpen(false);
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {preset.title}
                  </span>
                  {preset.chain ? (
                    <ChainStepsBadge count={preset.chain.steps.length} />
                  ) : null}
                </div>
                <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                  {preset.description ?? preset.promptText}
                </p>
              </button>
              {manage && tab !== "all" && tab !== "favourites" ? (
                <div className="flex shrink-0 flex-col">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => onMovePreset(preset.id, "up")}
                    className="text-muted-foreground/60 hover:text-foreground disabled:opacity-20"
                    title="Move up"
                  >
                    <ChevronUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={index === list.length - 1}
                    onClick={() => onMovePreset(preset.id, "down")}
                    className="text-muted-foreground/60 hover:text-foreground disabled:opacity-20"
                    title="Move down"
                  >
                    <ChevronDown className="size-3.5" />
                  </button>
                </div>
              ) : null}
            </div>
          ))}
          {hasNextPage && tab !== "favourites" ? (
            <div ref={loadMoreRef} className="py-2">
              {isFetchingNextPage ? <CommandSidebarListSkeleton count={2} /> : null}
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
    <SavePresetDialog
      open={saveOpen}
      onOpenChange={setSaveOpen}
      initialPrompt={currentPrompt}
      onSave={onCreatePreset}
    />
    </>
  );
}
